/**
 * Shared answer-normalization primitive for form-response matchers. Lives here (not in a
 * feature's `_logic`) so both `_lib` consumers (e.g. email-templates.ts) and feature consumers
 * (e.g. form-confirmation-rules.ts) can import it without a lib→feature dependency inversion.
 */

/** Unwraps the `{ value }` shape used by "Other" answers; returns the raw value otherwise. */
export const unwrapAnswer = (raw: unknown): unknown =>
  typeof raw === "object" && raw !== null ? (raw as { value?: unknown }).value : raw;

/**
 * Whether a stored answer matches any of a rule's trigger values. Handles single-select
 * (direct compare), multiselect (any chosen value listed), and the "Other" `{ value }`
 * shape, mirroring how `show_if` conditions match in conditional-visibility.ts.
 *
 * @param answer - The raw answer from `response_data` for the rule's field.
 * @param values - The rule's trigger values.
 * @returns True when the answer satisfies the rule.
 */
export const answerMatchesValues = (answer: unknown, values: string[]): boolean => {
  if (Array.isArray(answer)) return answer.some((item) => values.includes(String(unwrapAnswer(item))));
  const value = unwrapAnswer(answer);
  return value != null && values.includes(String(value));
};
